// Manager-app route tree.
// Composed from `sharedRoutes.jsx` plus manager-only pages.

import { Navigate } from 'react-router';

import { AppLayout } from 'app/layouts/AppLayout';
import { DynamicLayout } from 'app/layouts/DynamicLayout';
import OnboardingPage from 'app/pages/shared/onBoarding/onBoardingPage';
import TenantSelectorPage from 'app/pages/shared/TenantSelectorPage';
import AdminTenantGuard from 'middleware/AdminTenantGuard';
import AuthGuard from 'middleware/AuthGuard';
import ModuleRoleGuard from 'middleware/ModuleRoleGuard';

import {
  alertDetailsRoute,
  dashboardRoute,
  helpRoute,
  reportsRoutes,
  settingsRoutes,
} from '../sharedRoutes';

// ----------------------------------------------------------------------

// Manager exposes an extra /monitoring/* sub-tree built from shared report pages.
const managerMonitoringRoutes = {
  path: 'monitoring',
  children: [
    {
      index: true,
      element: <Navigate to="/monitoring/analytics" replace />,
    },
    {
      path: 'analytics',
      lazy: async () => ({
        Component: (await import('app/pages/shared/reports/DataAnalytics'))
          .default,
      }),
    },
    {
      path: 'daily-profile',
      lazy: async () => ({
        Component: (
          await import('app/pages/shared/reports/DailyProfile/DailyProfiles')
        ).default,
      }),
    },
    {
      path: 'weather',
      lazy: async () => ({
        Component: (
          await import(
            'app/pages/shared/reports/WeatherAnalysis/WeatherAnalysis'
          )
        ).default,
      }),
    },
    {
      path: 'annual',
      lazy: async () => ({
        Component: (await import('app/pages/shared/reports/AnnualQI/AnnualAqi'))
          .default,
      }),
    },
  ],
};

const protectedRoutes = {
  id: 'protected',
  Component: AuthGuard,
  children: [
    {
      path: 'select-tenant',
      element: <TenantSelectorPage />,
    },
    {
      Component: AdminTenantGuard,
      children: [
        {
          children: [
            {
              index: true,
              element: <OnboardingPage />,
            },
            {
              path: 'onboarding',
              element: <OnboardingPage />,
            },
          ],
        },
        {
          Component: ModuleRoleGuard,
          children: [
            {
              Component: DynamicLayout,
              children: [
                dashboardRoute,
                reportsRoutes,
                {
                  path: 'air-pollution',
                  lazy: async () => ({
                    Component: (await import('app/pages/shared/dashboards/home'))
                      .default,
                  }),
                },
                {
                  path: 'climate',
                  lazy: async () => ({
                    Component: (await import('app/pages/manager/climatePage')).default,
                  }),
                },
                {
                  path: 'urbanism',
                  lazy: async () => ({
                    Component: (await import('app/pages/manager/urbanismPage')).default,
                  }),
                },
                managerMonitoringRoutes,
                {
                  path: 'meteorology',
                  lazy: async () => ({
                    Component: (
                      await import('app/pages/manager/meteorologyPage')
                    ).default,
                  }),
                },
                {
                  path: 'health',
                  lazy: async () => ({
                    Component: (await import('app/pages/manager/healthPage')).default,
                  }),
                },
                {
                  path: 'daily-forecast',
                  lazy: async () => ({
                    Component: (
                      await import('app/pages/manager/dailyForecastPage')
                    ).default,
                  }),
                },
                {
                  path: 'station/:stationId/calibrations/:calibrationId',
                  lazy: async () => ({
                    Component: (
                      await import('app/pages/shared/devices/CalibrationDetails')
                    ).default,
                  }),
                },
                {
                  path: 'devices/calibration/add',
                  lazy: async () => ({
                    Component: (await import('app/pages/shared/devices/Calibration'))
                      .default,
                  }),
                },
                {
                  path: 'data-sharing',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="/data-sharing/export" replace />,
                    },
                    {
                      path: 'export',
                      lazy: async () => ({
                        Component: (
                          await import('app/pages/monitoring/dataSharing/ExportBuilderPage')
                        ).default,
                      }),
                    },
                    {
                      path: 'recent',
                      lazy: async () => ({
                        Component: (
                          await import('app/pages/monitoring/dataSharing/RecentExportsPage')
                        ).default,
                      }),
                    },
                  ],
                },
                alertDetailsRoute,
                helpRoute,
              ],
            },
            {
              Component: AppLayout,
              children: [settingsRoutes],
            },
          ],
        },
      ],
    },
  ],
};

export { protectedRoutes };
